import React, { useState, useEffect, useCallback, useMemo } from 'react'
import styled, { keyframes } from 'styled-components'

import {
  Row,
  Col,
  Card,
  CardBody,
  CardTitle,
  CardText,
  CardHeader,
  Button,
  Alert
} from 'reactstrap'

import Modal from 'ui/modal'
import CalendarPicker from 'components/calendar_picker'
import ProductCard from 'containers/list/products/product_card'
import ApiFetcher from 'lib/api_fetcher'
import time from 'i18n/time'
import { useTranslation } from 'react-i18next'

const AVAILABILITY_OPTIONS = [
  { code: 'orsag1', label: 'Sagrada Familia' }
]

const pulseRing = keyframes`
  0% {
    transform: scale(0.8);
    opacity: 1;
  }

  70% {
    transform: scale(2.8);
    opacity: 0;
  }

  100% {
    transform: scale(3.1);
    opacity: 0;
  }
`

const AvailabilityLiveDot = styled.span`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: ${props => props.color || '#22c55e'};
  flex-shrink: 0;

  &::before {
    content: '';
    position: absolute;
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: ${props => props.color || '#22c55e'};
    z-index: 2;
  }

  &::after {
    content: '';
    position: absolute;
    width: 10px;
    height: 10px;
    border-radius: 50%;
    border: 4px solid ${props => props.pulseColor || 'rgba(34, 197, 94, 0.55)'};
    animation: ${pulseRing} 1.8s infinite ease-out;
    z-index: 1;
  }
`

const AvailabilityModal = ({
  availabilityGroupCode,
  modal,
  toggle,
  urls
}) => {
  const { i18n } = useTranslation()

  const [isFetching, setIsFetching] = useState(false)
  const [isFetchingProducts, setIsFetchingProducts] = useState(false)

  const [response, setResponse] = useState(null)
  const [productsResponse, setProductsResponse] = useState(null)

  const [error, setError] = useState(null)
  const [productsError, setProductsError] = useState(null)

  const [activeMonth, setActiveMonth] = useState(
    time().startOf('month').format('YYYY-MM-DD')
  )

  const endpoint = useMemo(() => {
    if (!availabilityGroupCode) return null

    return urls.availability_group.replace(
      / *\:[^)]*\: */g,
      availabilityGroupCode
    )
  }, [urls.availability_group, availabilityGroupCode])

  const productsEndpoint = useMemo(() => {
    return urls.products_grid
  }, [urls.products_grid])

  const daysAvailable = useMemo(() => {
    return Object.keys(response?.availability || {})
  }, [response])

  const fetchAvailability = useCallback((date = activeMonth) => {
    if (!endpoint) return Promise.resolve()

    setIsFetching(true)
    setResponse(null)
    setError(null)
    setProductsResponse(null)
    setProductsError(null)

    PubSub.publish('loading-page')

    const startDate = time(date).startOf('month').format('YYYY-MM-DD')
    const endDate = time(date).endOf('month').format('YYYY-MM-DD')

    return new ApiFetcher({
      method: 'GET',
      key: 'availability_group',
      endpoint,
      query: {
        start_date: startDate,
        end_date: endDate
      }
    }).call()
      .then((res) => {
        if (res?.success === false) {
          setError(
            res?.message ||
            i18n.t('validations:availability_fetch_error')
          )

          return
        }

        setResponse(res)
      })
      .catch(() => {
        setError(i18n.t('validations:fetch_error'))
      })
      .finally(() => {
        PubSub.publish('loaded-page')
        setIsFetching(false)
      })
  }, [endpoint, activeMonth, i18n])

  const fetchProducts = useCallback((productIds) => {
    if (!productsEndpoint || !productIds?.length) {
      return Promise.resolve()
    }

    setIsFetchingProducts(true)
    setProductsResponse(null)
    setProductsError(null)

    PubSub.publish('loading-page')

    return new ApiFetcher({
      method: 'GET',
      key: 'products_grid',
      endpoint: productsEndpoint,
      query: {
        'ids[]': productIds
      }
    }).call()
      .then((res) => {
        if (res?.success === false) {
          setProductsError(
            res?.message ||
            i18n.t('validations:products_fetch_error')
          )

          return
        }

        setProductsResponse(res)
      })
      .catch(() => {
        setProductsError(i18n.t('validations:fetch_error'))
      })
      .finally(() => {
        PubSub.publish('loaded-page')
        setIsFetchingProducts(false)
      })
  }, [productsEndpoint, i18n])

  const handleDaySelected = useCallback((day) => {
    const selectedDate = time(day)
      .tz('Europe/Madrid')
      .format('YYYY-MM-DD')

    const productIds = response?.availability?.[selectedDate] || []

    fetchProducts(productIds)
  }, [response, fetchProducts])

  const handleMonthChange = useCallback((date) => {
    const newActiveMonth = time(date)
      .startOf('month')
      .format('YYYY-MM-DD')

    setActiveMonth(newActiveMonth)
    fetchAvailability(newActiveMonth)
  }, [fetchAvailability])

  useEffect(() => {
    if (!modal || !endpoint) return

    fetchAvailability(activeMonth)
  }, [modal, endpoint])

  return (
    <Modal
      header="Sagrada Familia"
      isOpen={modal}
      toggle={toggle}
    >
      {isFetching && (
        <div className="mt-3 alert alert-warning">
          {i18n.t('availability_group_widget.fetching_availability')}
        </div>
      )}

      {error && (
        <Alert color="danger">
          {error}
        </Alert>
      )}

      {response?.success === true && !error && (
        <CalendarPicker
          key={activeMonth}
          initialDay={activeMonth}
          onDaySelected={handleDaySelected}
          onMonthChange={handleMonthChange}
          daysAvailable={daysAvailable}
        />
      )}

      {isFetchingProducts && (
        <div className="mt-3 alert alert-warning">
          {i18n.t('availability_group_widget.fetching_products')}
        </div>
      )}

      {productsError && (
        <Alert color="danger" className="mt-3">
          {productsError}
        </Alert>
      )}

      {productsResponse?.pagination?.total_items >= 1 && (
        <div className="mt-5">
          <div className="h5 p-0 m-0">
            {i18n.t(
              'availability_group_widget.activities_available_count',
              {
                count: productsResponse.pagination.total_items
              }
            )}
          </div>

          <div className="row row-cols-1 row-cols-sm-2 row-cols-lg-3 row-cols-xl-4 g-3 p-0 mt-2">
            {productsResponse.products.map((product, key) => (
              <ProductCard
                {...product}
                key={key}
                new_window={true}
                urls={urls}
                url={urls.service.replace(
                  / *\:[^)]*\: */g,
                  product.slug
                )}
              />
            ))}
          </div>
        </div>
      )}

      {productsResponse?.pagination?.total_items == 0 && (
        <div className="mt-3 alert alert-warning">
          {i18n.t(
            'availability_group_widget.no_products_available'
          )}
        </div>
      )}
    </Modal>
  )
}

const AvailabilityGroupWidget = (props) => {
  const {
    urls,
    web_event_request,
    variant = 'card',
    fetchFirstDay = false
  } = props

  const [availabilityGroupCode, setAvailabilityGroupCode] = useState(null)
  const [modal, setModal] = useState(false)
  const [firstAvailability, setFirstAvailability] = useState()

  const fetchAvailabilityGroup = (endpoint, setFirstAvailability) => {
    const startDate = time().startOf('month').format('YYYY-MM-DD')
    const endDate = time().endOf('month').format('YYYY-MM-DD')

    return new ApiFetcher({
      method: 'GET',
      key: 'availability_group',
      endpoint,
      query: {
        start_date: startDate,
        end_date: endDate
      }
    }).call()
      .then((res) => {
        if (res?.success === true) {
          const availability = res.availability

          if (typeof availability === 'object' && availability !== null && Object.keys(availability).length > 0) {
            setFirstAvailability(time(Object.keys(availability)[0]).calendar(null, {
              sameDay: '[' + i18n.t('format:time.today') + ']',
              nextDay: '[' + i18n.t('format:time.tomorrow') + ']',
              nextWeek: 'DD/MM/YYYY',
              lastWeek: 'DD/MM/YYYY',
              sameElse: 'DD/MM/YYYY'
            }))
          }

          return
        }
      })
  }

  useEffect(() => {
    if (fetchFirstDay) {
      fetchAvailabilityGroup(urls.availability_group_orsag, setFirstAvailability)
    }
  }, [])

  const { i18n } = useTranslation()

  const closeModal = useCallback(() => {
    setModal(false)
  }, [])

  const openModal = useCallback((code) => {
    setAvailabilityGroupCode(code)
    setModal(true)
  }, [])

  const defaultOption = AVAILABILITY_OPTIONS[0]

  const publishAvailabilityGroupClick = (variant) => {
    const body = {
      key: 'availability_group_click',
      value: variant
    }

    new ApiFetcher({
      key: 'publish_event',
      endpoint: web_event_request.endpoint,
      method: web_event_request.method,
      body: JSON.stringify(body)
    }).call()
      .then((res) => {})
  }

  if (variant === 'banner') {
    return (
      <Card className="mb-3">
        <CardBody
          className="d-flex flex-column flex-md-row justify-content-between align-items-center"
          style={{ fontSize: '1.2em' }}
        >
          <div className="d-flex align-items-center">
            <AvailabilityLiveDot
              className="me-3"
              color="#22c55e"
              pulseColor="rgba(34, 197, 94, 0.55)"
            />

            <div>
              <strong className="me-2">
                Sagrada Familia:
              </strong>

              <span>
                {i18n.t('availability_group_widget.banner_description')}
              </span>
            </div>
          </div>

          <Button
            color="danger"
            className="fw-bold shadow mt-3 mt-md-0"
            onClick={() => {
              openModal(defaultOption.code)
              publishAvailabilityGroupClick('banner')
            }}
          >
            <i className="fas fa-hand-pointer me-2"/>

            {i18n.t(
              'availability_group_widget.availability_button'
            )}
          </Button>

          <AvailabilityModal
            {...props}
            modal={modal}
            toggle={closeModal}
            availabilityGroupCode={availabilityGroupCode}
          />
        </CardBody>
      </Card>
    )
  }

  return (
    <Card className="border-danger mb-3 shadow">
      <CardHeader className="bg-danger text-white">
        <span className="fw-bold d-flex align-items-center">
          <AvailabilityLiveDot
            className="me-3"
            color="#ffffff"
            pulseColor="rgba(255, 255, 255, 0.7)"
          />

          {i18n.t(
            'availability_group_widget.widget_header'
          )}
        </span>
      </CardHeader>

      <CardBody>
        <CardTitle tag="h5">
          Sagrada Familia
        </CardTitle>

        <CardText>
          {i18n.t(
            'availability_group_widget.widget_description'
          )}
          {firstAvailability &&
            <div className="mt-2">{i18n.t('availability_group_widget.nearest_availability')}: <strong className="text-success">{firstAvailability}</strong></div>
          }
        </CardText>

        <Row>
          {AVAILABILITY_OPTIONS.map((option) => (
            <Col key={option.code}>
              <Button
                color="danger"
                className="w-100 fw-bold d-flex align-items-center justify-content-center"
                onClick={() => {
                  openModal(option.code)
                  publishAvailabilityGroupClick('card')
                }}
              >
                <i className="fas fa-hand-pointer me-2"/>

                {i18n.t(
                  'availability_group_widget.availability_button'
                )}
              </Button>
            </Col>
          ))}
        </Row>
      </CardBody>

      <AvailabilityModal
        {...props}
        modal={modal}
        toggle={closeModal}
        availabilityGroupCode={availabilityGroupCode}
      />
    </Card>
  )
}

export default AvailabilityGroupWidget
